import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Error } from 'components/Common/Error';
import { format, parse } from 'date-fns';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_PICKER_FORMAT_MONTH } from '../../../../../../../../@constants/date';
import DatePicker from '../../../../../../../../components/DatePicker/DatePicker';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { RateField } from '../../../../../../components/Inputs/RateField';
import { RoomTypeField } from '../../../../../../components/Inputs/RoomTypeField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { FilterEnum, FourWeekForecastReportFormModel, GroupByEnum } from '../../models';
import { CompanyField } from './fields/CompanyField';
import { CurrencyApplicationField } from './fields/currencyApplicationField';
import { CurrencyField } from './fields/CurrencyField';
import { FixedAtField } from './fields/FixedAtField';

export const FilterForm: FC<{
  onSubmit: (data: FourWeekForecastReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const { control, handleSubmit, setValue, watch } =
    useFormContext<FourWeekForecastReportFormModel>();

  const formValues = watch();

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Four Week Forecast" />

        <Stack direction={'column'} spacing={2} p={3}>
          <Controller
            control={control}
            name="month"
            render={({ field, formState }) => (
              <DatePicker
                value={
                  field.value ? parse(field.value, DATE_PICKER_FORMAT_MONTH, new Date()) : null
                }
                onChange={(newValue: string) => {
                  if (!newValue) {
                    field.onChange(null);
                    return;
                  }
                  const dateObj = new Date(newValue);
                  const formattedValue = format(dateObj, DATE_PICKER_FORMAT_MONTH);
                  field.onChange(formattedValue);
                }}
                views={['year', 'month']}
                openTo="month"
                inputFormat={DATE_PICKER_FORMAT_MONTH}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    id={`month-datepicker`}
                    {...params}
                    variant="standard"
                    label="Month"
                    error={Boolean(formState.errors.month)}
                    helperText={formState.errors.month?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />

          <Controller
            control={control}
            name="company"
            render={({ field }) => (
              <CompanyField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <Controller
            control={control}
            name="rates"
            render={({ field, formState }) => (
              <RateField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />

          {/* <Controller
            control={control}
            name="roomType"
            render={({ field }) => (
              <RoomTypeField value={field.value} onChange={value => field.onChange(value)} />
            )}
          /> */}

          <Controller
            control={control}
            name="roomTypeIds"
            render={({ field }) => (
              <RoomTypeField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <FormControl>
            <InputLabel
              sx={{
                pt: 2,
              }}
            >
              Group By
            </InputLabel>
            <Controller
              control={control}
              name="group_by"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  row
                  sx={{
                    pt: 2,
                  }}
                >
                  <FormControlLabel
                    value={GroupByEnum.MARKET_SEGMENT}
                    control={<Radio />}
                    label="Market Segment"
                  />
                  <FormControlLabel value={GroupByEnum.MARKET} control={<Radio />} label="Market" />
                </RadioGroup>
              )}
            />
          </FormControl>
          {/* <Divider />
          <FormControl>
            <InputLabel
              sx={{
                pt: 2,
              }}
            >
              Allocation / Guarantee
            </InputLabel>
            <Controller
              control={control}
              name="sub_filter"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  row
                  sx={{
                    pt: 2,
                  }}
                >
                  <FormControlLabel value={SubFilterEnum.NONE} control={<Radio />} label="None" />
                  <FormControlLabel
                    value={SubFilterEnum.ALLOCATION}
                    control={<Radio />}
                    label="Allocation"
                  />
                  <FormControlLabel
                    value={SubFilterEnum.GUARANTEE}
                    control={<Radio />}
                    label="Guarantee"
                  />
                </RadioGroup>
              )}
            />
          </FormControl> */}
          <Divider />
          <Box sx={{ height: 2 }} />
          <FormControl>
            <InputLabel>Type</InputLabel>
            <Controller
              control={control}
              name="filter"
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel value={FilterEnum.ROOM} control={<Radio />} label="Room" />
                  <FormControlLabel value={FilterEnum.PAX} control={<Radio />} label="Pax" />
                </RadioGroup>
              )}
            />
          </FormControl>
          <Divider />

          <Controller
            control={control}
            name="with_revenue"
            render={({ field }) => (
              <FormControlLabel
                sx={{ m: 0 }}
                {...field}
                checked={field.value ?? false}
                onChange={(e, checked) => field.onChange(checked)}
                label={
                  <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                    With Revenue
                  </Typography>
                }
                control={<Switch />}
              />
            )}
          />

          <Controller
            control={control}
            name="currency"
            render={({ field, formState }) => (
              <CurrencyField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />

          <Controller
            control={control}
            name="currency_application"
            render={({ field, formState }) => (
              <CurrencyApplicationField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />

          <Controller
            control={control}
            name="fixed_at"
            render={({ field, formState }) => (
              <FixedAtField
                value={field.value}
                onChange={value => field.onChange(value)}
                helperText={
                  <>
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  </>
                }
              />
            )}
          />

          <Controller
            control={control}
            name="gross_revenue"
            render={({ field }) => (
              <FormControlLabel
                sx={{ m: 0 }}
                {...field}
                checked={field.value ?? false}
                disabled={formValues.with_revenue === false}
                onChange={(e, checked) => field.onChange(checked)}
                label={
                  <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                    Gross Revenue
                  </Typography>
                }
                control={<Switch />}
              />
            )}
          />
        </Stack>

        {/* <FooterButtons onSubmit={handleSubmit(onSubmit)} onReset={onReset} /> */}
        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
